import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

import { useShareContactsMutation } from 'services/userV2';
import { useUpdateUserMutation } from 'services/user';
import { trackEvent } from 'helpers/analytics';
import { setUser } from 'actions/userActions';
import { legalDocumentsVersion } from 'settings/legalDocumentsVersion';
import strings from 'strings';

export const useAllyIntegrations = ({ network }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [shareContacts, { isLoading: isShareContactsLoading }] = useShareContactsMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();

  const isLoading = isShareContactsLoading || isUpdatingUser;

  const completeOnboarding = useCallback(async () => {
    const { data, error } = await updateUser({
      onboardingCompleted: true,
      privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
      termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
    });

    if (error) {
      toast(strings.genericError, { icon: 'error', id: 'complete-onboarding' });
      return false;
    }

    if (data) {
      dispatch(setUser(data));
    }
    return true;
  }, [updateUser, dispatch]);

  const handleComplete = useCallback(async () => {
    // Auto-share contacts with the network
    const { data, error } = await shareContacts({
      collectionId: network?.id,
      share: true,
    });

    if (error) {
      toast(strings.genericError, { icon: 'error', id: 'share-contacts' });
      return;
    }

    if (data) {
      const success = await completeOnboarding();
      if (!success) return;

      trackEvent('ally_onboarding:completed', {
        collection_id: network?.id,
      });

      history.push(`/onboarding/${network?.slug}/ally-signup/complete`);
    }
  }, [shareContacts, network?.id, network?.slug, completeOnboarding, history]);

  const handleGoogleSyncComplete = useCallback(() => {
    trackEvent('ally_onboarding:google_sync_complete', {
      collection_id: network?.id,
    });
  }, [network?.id]);

  return {
    isLoading,
    handleContinue: handleComplete,
    handleSkip: handleComplete,
    handleGoogleSyncComplete,
  };
};
